import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { PersonalInfoFields } from "./forms/PersonalInfoFields";
import { HTTPError } from 'ky';
import { PromoCodeSection } from "./forms/PromoCodeSection";
import { ConsentSection } from "./forms/ConsentSection";
import { CustomerFormData, customerFormSchema } from "./forms/types";
import { api } from "./client/backend";

interface CustomerProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
  phone_country_code?: string;
  marketing_opt_in?: boolean;
  privacy_accepted?: boolean;
}

interface CustomerFormProps {
  onSubmit: (data: CustomerFormData & { customerProfileId?: string }) => void;
  eventId: string;
  subtotal: number;
  onPromoCodeApplied: (discountPercentage: number) => void;
}

export const CustomerForm = ({
  onSubmit,
  eventId,
  subtotal,
  onPromoCodeApplied
}: CustomerFormProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const form = useForm<CustomerFormData>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneCountryCode: "+61",
      phoneNumber: "",
      marketingConsent: true,
      termsAccepted: true,
      promoCode: "",
    },
  });

  const handleSubmit = async (data: CustomerFormData) => {
    if (!data.termsAccepted) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const normalizedEmail = data.email.toLowerCase().trim();
      console.log("Starting customer profile check with email:", normalizedEmail);

      let existingProfile;
      try {
        // Check if customer profile exists
        existingProfile = await api.get('customer_profiles', {
          searchParams: { email: normalizedEmail }
        }).json();
        console.log("Profile check result:", existingProfile);
      } catch (error) {
        if (error instanceof HTTPError && error.response.status === 404) {
          console.log("No existing profile found");
          existingProfile = null; // Profile not found, proceed to create
        } else {
          console.error("Error checking existing profile:", error);
          throw new Error(`Failed to check existing profile: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

      let profileId;

      if (existingProfile) {
        console.log("Using existing profile:", existingProfile);
        profileId = existingProfile.id;
      } else {
        console.log("Creating new customer profile");
        // Create new profile
        const newProfile = await api.post<CustomerProfile>('customer_profiles', {
          json: {
            email: normalizedEmail,
            first_name: data.firstName,
            last_name: data.lastName,
            phone_number: data.phoneNumber,
            phone_country_code: data.phoneCountryCode,
            marketing_opt_in: data.marketingConsent,
            privacy_accepted: data.termsAccepted
          }
        }).json();

        console.log("New profile created successfully:", newProfile);
        profileId = newProfile.id;
      }

      console.log("Customer profile processed successfully with ID:", profileId);

      // Pass the data with the customer profile ID and individual user fields
      onSubmit({
        ...data,
        customerProfileId: profileId
      });
    } catch (error) {
      console.error("Customer profile processing error:", error);
      let errorMsg = "Failed to process customer profile. Please try again.";
      if (error instanceof HTTPError) {
        errorMsg = `Failed to process customer profile: ${error.response.status}`;
        // Optionally, parse error body for more details
        // const errorBody = await error.response.json();
        // errorMsg = `Failed to process customer profile: ${error.response.status} - ${errorBody.message}`;
      } else if (error instanceof Error) {
        errorMsg = error.message;
      }
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <PersonalInfoFields form={form} />

        <PromoCodeSection
          form={form}
          eventId={eventId}
          onPromoCodeApplied={onPromoCodeApplied}
        />

        <ConsentSection form={form} />

        <Button
          type="submit"
          disabled={isProcessing}
          className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
        >
          {isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            "Continue to Payment"
          )}
        </Button>

        {errorMessage && (
          <div className="mt-2 text-sm font-medium" style={{ color: "#dc143c" }}>
            {errorMessage}
          </div>
        )}
      </form>
    </Form>
  );
};
